import type { Catalog } from "../en";

/** Filipino: roles page + Team members tab (mirrors en/roles.ts). */
export const roles: Catalog["roles"] = {
  title: "Mga role at permission",
  subtitle: "Pagpasyahan ang magagawa ng bawat role, limitahan ito sa mga agent o team, at italaga sa mga miyembro.",
  backToTeam: "Team",
  newRole: "Bagong role",
  listLabel: "Mga role",
  builtIn: "Built-in",
  custom: "Custom",
  locked: "Naka-lock",
  lockedHint: "Hindi mae-edit ang built-in na role. I-duplicate ito para i-customize.",
  members_one: "{{count}} miyembro",
  members_other: "{{count}} miyembro",
  permissions_one: "{{count}} permission",
  permissions_other: "{{count}} permission",
  duplicate: "I-duplicate",
  duplicateName: "{{name}} (kopya)",
  duplicated: "Na-duplicate ang role — palitan ang pangalan at ayusin ang mga permission",
  delete: "Burahin ang role",
  emptyTitle: "Pumili ng role",
  emptyBody: "Pumili ng role para makita ang mga permission nito, o gumawa ng bago.",
  loadError: "Hindi ma-load ang mga role.",
  notFound: "Wala na ang role na iyon.",
  editor: {
    createTitle: "Gumawa ng role",
    editTitle: "I-edit ang role",
    viewTitle: "{{name}}",
    name: "Pangalan ng role",
    namePlaceholder: "hal. Front desk",
    nameRequired: "Bigyan ng pangalan ang role",
    description: "Paglalarawan",
    descriptionPlaceholder: "Para saan ang role na ito?",
    startFrom: "Simulan mula sa",
    startBlank: "Blangkong role",
    startFromRole: "Kopya ng {{name}}",
    save: "I-save ang role",
    create: "Gumawa ng role",
    cancel: "Kanselahin",
    discard: "Itapon ang mga pagbabago",
    saved: "Na-save ang role",
    created: "Nagawa ang role na “{{name}}”",
    unsaved: "May hindi pa na-save",
    auditNote: "Itinatala sa audit log ang bawat pagbabago sa mga role at assignment.",
  },
  matrix: {
    title: "Mga permission",
    subtitle: "Mga module sa gilid, mga aksyon sa itaas. Ang “—” ay nangangahulugang walang ganoong aksyon ang module.",
    module: "Module",
    unavailable: "Hindi available sa module na ito",
    toggle: "{{module}}: {{action}}",
    selectRow: "Lahat ng permission ng {{module}}",
    notHeld: "Hindi ka makakapagbigay ng permission na wala sa iyo",
    legendAllowed: "Pinapayagan",
    legendNone: "Hindi pinapayagan",
    legendNa: "Hindi applicable",
  },
  modules: {
    workspace: "Workspace",
    agents: "Mga agent",
    playground: "Playground",
    knowledge: "Kaalaman",
    actions: "Mga aksyon",
    evaluations: "Mga ebalwasyon",
    widget: "Widget",
    channels: "Mga channel",
    inbox: "Inbox",
    leads: "Leads",
    gaps: "Knowledge gaps",
    announcements: "Mga anunsyo",
    analytics: "Analytics",
    audiences: "Mga audience",
    team: "Team",
    roles: "Mga role",
    billing: "Billing",
    settings: "Settings",
    api_keys: "API keys",
    webhooks: "Webhooks",
    integrations: "Mga integration",
    audit: "Audit log",
    data: "Datos",
    tickets: "Mga ticket",
    organisations: "Mga organisasyon",
    assets: "Mga asset",
    visits: "Mga pagbisita sa field",
    orders: "Mga order",
    connectors: "Mga connector",
    customers: "Mga customer",
  },
  actions: {
    view: "Tingnan",
    create: "Gumawa",
    edit: "I-edit",
    delete: "Burahin",
    publish: "I-publish",
    use: "Gamitin",
    reindex: "I-reindex",
    manage: "Pamahalaan",
    run: "Patakbuhin",
    reply: "Sumagot",
    takeover: "Saluhin",
    assign: "Italaga",
    export: "I-export",
    resolve: "Lutasin",
    send: "Ipadala",
    invite: "Mag-imbita",
    note: "Tala",
    configure: "I-configure",
    reports: "Mga ulat",
    perform: "Isagawa",
    approve: "Aprubahan",
    view_reference: "Tingnan ang reference",
    manage_launch: "Pamahalaan ang secure launch",
    publish_override: "Override sa pag-publish",
  },
  effective: {
    title: "Epektibong mga permission",
    subtitle: "Ang ibibigay ng role na ito kapag na-save, sinuri ng server.",
    memberTitle: "Epektibong mga permission ni {{name}}",
    memberSubtitle: "Pinagsamang lahat ng assignment sa ibaba, para sa napiling saklaw.",
    count: "{{granted}} sa {{total}} permission",
    loading: "Sinusuri sa server…",
    error: "Hindi ma-load ang preview. Puwede ka pa ring mag-save.",
    previewFor: "Preview para sa",
    anyScope: "Workspace (lahat ng agent)",
    forAgent: "Agent: {{name}}",
    forTeam: "Team: {{name}}",
    none: "Wala pang permission na ibinibigay ang role na ito.",
  },
  scope: {
    title: "Saklaw",
    all: "Lahat ng agent",
    allHint: "Para sa buong workspace.",
    agents: "Piling mga agent",
    agentsHint: "Para lang sa mga usapan, kaalaman at settings ng mga napiling agent.",
    teams: "Piling mga team",
    teamsHint: "Para lang sa mga handoff na ipinasa sa mga napiling team.",
    chooseAgents: "Pumili ng mga agent",
    chooseTeams: "Pumili ng mga team",
    searchAgents: "Maghanap ng agent…",
    searchTeams: "Maghanap ng team…",
    noAgents: "Wala pang agent",
    noTeams: "Wala pang team",
    selected_one: "{{count}} napili",
    selected_other: "{{count}} napili",
    agentsUnavailable: "Hindi pa available ang listahan ng agent — limitahan muna sa team o sa lahat ng agent.",
    pickOne: "Pumili ng kahit isa",
    summaryAll: "Lahat ng agent",
    summaryAgents_one: "{{count}} agent",
    summaryAgents_other: "{{count}} agent",
    summaryTeams_one: "{{count}} team",
    summaryTeams_other: "{{count}} team",
  },
  assign: {
    title: "Mga miyembrong may role na ito",
    empty: "Wala pang may role na ito.",
    add: "Italaga sa miyembro",
    dialogTitle: "Italaga ang {{role}}",
    dialogSubtitle: "Mananatili ang iba pang role ng miyembro. Ang epektibong permission ay pinagsamang lahat ng assignment.",
    member: "Miyembro",
    chooseMember: "Pumili ng miyembro",
    searchMembers: "Maghanap ng miyembro…",
    noMembers: "Walang nahanap na miyembro",
    submit: "Italaga ang role",
    assigned: "Naitalaga ang {{role}} kay {{name}}",
    remove: "Alisin sa role",
    removed: "Inalis ang {{role}} kay {{name}}",
    you: "(ikaw)",
    ownRole: "Hindi mo mababago ang sarili mong mga role",
  },
  deleteDialog: {
    title: "Burahin ang “{{name}}”?",
    body: "Mawawala sa mga miyembro ang mga permission ng role na ito. Hindi na ito maibabalik.",
    inUse_one: "May {{count}} miyembrong may role na ito. Pumili muna ng role na paglilipatan sa kanila.",
    inUse_other: "May {{count}} miyembrong may role na ito. Pumili muna ng role na paglilipatan sa kanila.",
    reassignTo: "Ilipat ang mga miyembro sa",
    chooseRole: "Pumili ng role",
    submit: "Burahin ang role",
    deleted: "Nabura ang role",
  },
  planGate: {
    badge: "Pro plan",
    title: "Pro feature ang custom roles",
    body: "Naka-{{plan}} ka, na may limang built-in na role: Owner, Admin, Editor, Agent at Viewer. Mag-upgrade sa Pro o Institution para makagawa ng role, malimitahan ito sa mga agent o team, at maitalaga sa mga miyembro.",
    upgrade: "Ikumpara ang mga plano",
    builtInsBelow: "Nakalista sa ibaba ang mga built-in na role bilang sanggunian.",
  },
  guardrails: {
    grantNotHeld: "Hindi ka makakapagbigay ng permission na wala sa iyo.",
    selfRoleEdit: "Hindi mo mababago ang sarili mong role. Lumapit sa ibang Owner o Admin.",
    lastOwner: "Kailangang laging may kahit isang Owner ang workspace.",
    roleLocked: "Hindi mababago o mabubura ang mga built-in na role.",
    roleInUse: "May nakatalaga sa role na ito. Pumili muna ng role na paglilipatan ng mga miyembro.",
    conflict: "May role na sa pangalang ito.",
    readOnly: "Read-only ang session na ito.",
  },
};
